import { useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { niceDate, pill, shortLabel } from "@/lib/scoring";

const COLORS = ["#2563EB", "#0B0F19", "#60A5FA", "#94A3B8", "#1E3A8A", "#38BDF8"];
const tick = { fontSize: 11, fill: "#64748B" };

export function CategoryChart({ data, outcomes }) {
  const [mode, setMode] = useState("raw");
  const hist = data.history;
  const rows = mode === "session"
    ? hist.map((h) => ({ name: niceDate(h.date), ...Object.fromEntries(outcomes.map((o) => [shortLabel(o), h.counts[o.key]])) }))
    : outcomes.map((o) => ({ name: shortLabel(o), value: mode === "pct" ? data.overview.pct[o.key] : data.overview.counts[o.key] }));
  return (
    <div data-testid="category-chart" className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-extrabold text-[#0B0F19]">Results breakdown</p>
        <div className="flex gap-1.5">{[["raw", "Totals"], ["pct", "Percent"], ["session", "By session"]].map(([k, l]) => <button key={k} data-testid={`chart-mode-${k}`} onClick={() => setMode(k)} className={pill(mode === k)}>{l}</button>)}</div>
      </div>
      <div className="mt-4 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ left: -10, right: 8 }}>
            <CartesianGrid stroke="#F1F5F9" vertical={false} />
            <XAxis dataKey="name" tick={tick} interval={0} angle={mode === "session" ? 0 : -12} height={40} />
            <YAxis tick={tick} allowDecimals={mode === "pct"} unit={mode === "pct" ? "%" : ""} />
            <Tooltip formatter={(v) => (mode === "pct" ? `${v}%` : v)} cursor={{ fill: "#EFF6FF" }} />
            {mode === "session" ? <>{outcomes.map((o, i) => <Bar key={o.key} dataKey={shortLabel(o)} stackId="a" fill={COLORS[i % COLORS.length]} />)}<Legend wrapperStyle={{ fontSize: 11 }} /></>
              : <Bar dataKey="value" name={mode === "pct" ? "% of attempts" : "Count"} fill="#2563EB" radius={[6, 6, 0, 0]} />}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Each scale family gets its own chart so totals, 0–100 averages/percents and counts are never mixed on one axis.
const FAMILIES = (outcomes) => [
  { id: "points", title: "Points", metrics: [["total", "Session total"], ["cum_total", "Cumulative total"]] },
  { id: "avg", title: "Averages & percentages (0–100)", metrics: [["avg", "Session average"], ["cum_avg", "Cumulative average"],
    ...(outcomes.some((o) => o.key.startsWith("strike")) ? [["strike_pct", "Strike %"], ["located_strike_pct", "Located strike %"], ["ball_pct", "Ball %"]] : []), ...outcomes.map((o) => [`pct.${o.key}`, `${shortLabel(o)} %`])] },
  { id: "count", title: "Result counts per session", metrics: outcomes.map((o) => [`counts.${o.key}`, shortLabel(o)]) },
];

export function Timeline({ data, outcomes }) {
  const [on, setOn] = useState(["avg", "cum_avg"]);
  const fams = FAMILIES(outcomes);
  const rows = data.history.map((h) => ({ ...h, label: niceDate(h.date) }));
  const toggle = (k) => setOn((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));
  return (
    <div data-testid="timeline" className="rounded-2xl border border-slate-200 bg-white p-5">
      <p className="text-sm font-extrabold text-[#0B0F19]">Performance over time</p>
      <div className="mt-3 space-y-2">
        {fams.map((f) => (
          <div key={f.id} className="flex flex-wrap items-center gap-1.5">
            <span className="w-full sm:w-auto text-[11px] font-bold uppercase tracking-wider text-slate-400 mr-1">{f.title}</span>
            {f.metrics.map(([k, l]) => <button key={k} data-testid={`metric-${k.replace(".", "-")}`} onClick={() => toggle(k)} className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${on.includes(k) ? "bg-[#2563EB] text-white" : "border border-slate-200 text-slate-600"}`}>{l}</button>)}
          </div>
        ))}
      </div>
      {!rows.length && <p className="mt-4 text-sm text-slate-500">No sessions in this date range.</p>}
      {fams.filter((f) => f.metrics.some(([k]) => on.includes(k))).map((f) => (
        <div key={f.id} data-testid={`timeline-${f.id}`} className="mt-5">
          <p className="text-xs font-bold text-slate-500">{f.title}</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={{ left: -10, right: 8 }}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="label" tick={tick} />
                <YAxis tick={tick} domain={f.id === "avg" ? [0, 100] : ["auto", "auto"]} allowDecimals={f.id !== "count"} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {f.metrics.filter(([k]) => on.includes(k)).map(([k, l], i) => <Line key={k} type="monotone" dataKey={k} name={l} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ))}
    </div>
  );
}
